import React from 'react';
import { Container, Heading, Text, VStack, Link, Box, Divider, useColorMode } from '@chakra-ui/react';
// Jméno, IČO, adresa a e-mail se mění v src/data/business.ts
import { OWNER_NAME, ICO, BUSINESS_ADDRESS, CONTACT_EMAIL } from '../data/business';

const PrivacyPolicy: React.FC = () => {
  const { colorMode } = useColorMode();
  const textColor = colorMode === 'dark' ? 'gray.300' : 'gray.700';
  const headingColor = colorMode === 'dark' ? 'white' : 'black';

  return (
    <Container maxW="760px" py={16} px={{ base: 5, md: 8 }}>
      <VStack spacing={8} align="start">

        {/* Zpět */}
        <Link
          href="/"
          fontSize="sm"
          color={colorMode === 'dark' ? 'gray.400' : 'gray.500'}
          letterSpacing="1px"
          textTransform="uppercase"
          _hover={{ color: headingColor }}
        >
          ← Zpět
        </Link>

        <Box>
          <Heading as="h1" size="xl" fontWeight={300} color={headingColor} letterSpacing="2px" mb={2}>
            Zásady ochrany osobních údajů
          </Heading>
          <Text fontSize="sm" color={colorMode === 'dark' ? 'gray.500' : 'gray.400'}>
            Poslední aktualizace: 10. října 2026
          </Text>
        </Box>

        <Divider borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.200'} />

        {/* 1. Správce */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            1. Správce osobních údajů
          </Heading>
          <Text color={textColor} lineHeight={1.8}>
            Správcem vašich osobních údajů je:
          </Text>
          <VStack align="start" pl={4} mt={2} spacing={1}>
            <Text color={textColor}><strong>{OWNER_NAME}</strong></Text>
            {ICO && <Text color={textColor}>IČO: {ICO}</Text>}
            <Text color={textColor}>
              {BUSINESS_ADDRESS
                ? `Místo podnikání: ${BUSINESS_ADDRESS}`
                : 'Orlické hory, Česká republika'}
            </Text>
            <Text color={textColor}>E-mail: <Link href={`mailto:${CONTACT_EMAIL}`} color={colorMode === 'dark' ? 'gray.300' : 'gray.700'} textDecoration="underline">{CONTACT_EMAIL}</Link></Text>
          </VStack>
        </Box>

        {/* 2. Jaké údaje */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            2. Jaké osobní údaje zpracováváme
          </Heading>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Zpracováváme pouze údaje, které nám dobrovolně poskytnete prostřednictvím kontaktního 
            nebo rezervačního formuláře. V kontaktním formuláři jde o:
          </Text>
          <VStack align="start" pl={4} spacing={1}>
            <Text color={textColor}>• Jméno a příjmení</Text>
            <Text color={textColor}>• E-mailová adresa</Text>
            <Text color={textColor}>• Typ požadovaného fotografování</Text>
            <Text color={textColor}>• Preferovaný termín</Text>
            <Text color={textColor}>• Zpráva – libovolné informace, které nám sdělíte</Text>
            <Text color={textColor}>• Zdroj, odkud jste se o nás dozvěděli (nepovinné)</Text>
          </VStack>
          <Text color={textColor} lineHeight={1.8} mt={3} mb={2}>
            Při rezervaci termínu vánočního focení přes rezervační formulář jde o:
          </Text>
          <VStack align="start" pl={4} spacing={1}>
            <Text color={textColor}>• Jméno a příjmení</Text>
            <Text color={textColor}>• E-mailová adresa</Text>
            <Text color={textColor}>• Telefonní číslo</Text>
            <Text color={textColor}>• Vybraný termín focení</Text>
            <Text color={textColor}>• Poznámka – libovolné informace, které nám sdělíte (nepovinné)</Text>
            <Text color={textColor}>• Stav platby zálohy (zda byla záloha uhrazena)</Text>
          </VStack>
          <Text color={textColor} lineHeight={1.8} mt={3}>
            Žádné další osobní údaje automaticky neshromažďujeme. Web nepoužívá žádné analytické nástroje 
            (Google Analytics, Facebook Pixel apod.) ani žádné reklamní sledovací systémy.
          </Text>
        </Box>

        {/* 3. Účel a právní základ */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            3. Účel a právní základ zpracování
          </Heading>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Vaše osobní údaje zpracováváme výhradně za účelem:
          </Text>
          <VStack align="start" pl={4} spacing={1}>
            <Text color={textColor}>• Odpovědi na váš dotaz nebo poptávku</Text>
            <Text color={textColor}>• Domluvy termínu a podmínek fotografování</Text>
            <Text color={textColor}>• Případné komunikace v průběhu spolupráce</Text>
            <Text color={textColor}>• Rezervace termínu vánočního focení a jeho realizace (včetně evidence zálohy)</Text>
          </VStack>
          <Text color={textColor} lineHeight={1.8} mt={3}>
            Právním základem zpracování je váš souhlas udělený odesláním kontaktního formuláře 
            (čl. 6 odst. 1 písm. a) GDPR), resp. plnění smlouvy nebo příprava smlouvy na vaši žádost 
            (čl. 6 odst. 1 písm. b) GDPR).
          </Text>
          <Text color={textColor} lineHeight={1.8} mt={3}>
            Údaje z rezervace vánočního focení zpracováváme za účelem rezervace a realizace focení – 
            právním základem je plnění smlouvy (čl. 6 odst. 1 písm. b) GDPR).
          </Text>
        </Box>

        {/* 4. Cookies a sledování */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            4. Cookies a sledovací technologie
          </Heading>
          <Text color={textColor} lineHeight={1.8} mb={3}>
            Tento web <strong>nepoužívá žádné sledovací ani analytické cookies</strong>. Nejsou instalovány 
            žádné skripty třetích stran za účelem analýzy návštěvnosti, remarketingu ani jiného sledování chování uživatelů.
          </Text>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Web využívá výhradně technicky nezbytné ukládání v prohlížeči:
          </Text>
          <VStack align="start" pl={4} spacing={2}>
            <Box>
              <Text color={headingColor}>Preferovaný barevný motiv (světlý/tmavý)</Text>
              <Text color={textColor} fontSize="sm">
                Uloženo v <code>localStorage</code> vašeho prohlížeče čistě pro vaše pohodlí.
                Neobsahuje žádné osobní údaje, neopouští váš prohlížeč a nesdílí se s nikým.
              </Text>
            </Box>
            {/* Lišta je v CookieConsent.tsx (klíč noticeDismissed) */}
            <Box>
              <Text color={headingColor}>Skrytí informační lišty</Text>
              <Text color={textColor} fontSize="sm">
                Po kliknutí na „Rozumím“ si prohlížeč do <code>localStorage</code> uloží, že lištu
                o ochraně údajů už nemá zobrazovat. Neobsahuje žádné osobní údaje a neopouští váš prohlížeč.
              </Text>
            </Box>
          </VStack>
          <Text color={textColor} lineHeight={1.8} mt={3}>
            Technicky nezbytné uložení dat v prohlížeči nepodléhá povinnosti žádat souhlas dle 
            § 89 zákona č. 127/2005 Sb. (zákon o elektronických komunikacích) ani dle směrnice 2002/58/ES (ePrivacy).
          </Text>
        </Box>

        {/* 5. Příjemci */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            5. Příjemci osobních údajů
          </Heading>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Vaše osobní údaje nejsou prodávány ani sdíleny s třetími stranami pro jejich vlastní účely.
          </Text>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Kontaktní formulář je zpracováván výhradně naším vlastním serverovým backendem provozovaným 
            správcem. Zpráva je doručena přímo na e-mailový účet fotografky bez předávání jakékoli 
            třetí straně pro analytické ani marketingové účely.
          </Text>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Údaje z rezervace vánočního focení jsou uloženy na serveru našeho backendu, který běží 
            v České republice (hosting Rosti.cz, v rámci EHP). Třetím stranám je nepředáváme; 
            potvrzení rezervace vám a fotografce se odesílá e-mailem.
          </Text>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            S technickým provozem nám pomáhají tito zpracovatelé, kteří smějí s údaji nakládat 
            jen v nezbytném rozsahu a výhradně pro daný účel:
          </Text>
          <VStack align="start" pl={4} spacing={1} mb={3}>
            <Text color={textColor}>
              • Poskytovatel hostingu serveru – Rosti.cz (Česká republika), na jehož serveru 
              běží backend webu a jsou uložené údaje z rezervací.
            </Text>
            <Text color={textColor}>
              • Technický správce webu, který zajišťuje provoz webu a serveru. K údajům může mít 
              přístup výhradně za tímto účelem. Kvůli kontrole funkčnosti dostává kopie zpráv
              z kontaktního formuláře a upozornění na nové rezervace.
            </Text>
            {/* Plyne z CONTACT_EMAIL (@gmail.com) v src/data/business.ts – při změně
                schránky upravit i tady a odstavec o třetích zemích níž */}
            <Text color={textColor}>
              • Poskytovatel e-mailové schránky fotografky – Google (služba Gmail), kam se doručují
              zprávy z kontaktního formuláře, upozornění na rezervace a další e-mailová komunikace.
            </Text>
          </VStack>
          <Text color={textColor} lineHeight={1.8}>
            Google může údaje v e-mailové schránce zpracovávat i mimo Evropský hospodářský prostor;
            takové předání se řídí zárukami podle GDPR, které Google ke svým službám poskytuje
            (podrobnosti v{' '}
            <Link href="https://policies.google.com/privacy?hl=cs" isExternal color={colorMode === 'dark' ? 'gray.300' : 'gray.700'} textDecoration="underline">
              zásadách ochrany soukromí Google
            </Link>
            ). Jinak vaše údaje do zemí mimo Evropský hospodářský prostor nepředáváme.
          </Text>
        </Box>

        {/* 6. Doba uchování */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            6. Doba uchování údajů
          </Heading>
          <Text color={textColor} lineHeight={1.8}>
            Osobní údaje uchováváme po dobu nezbytně nutnou k vyřízení vašeho dotazu, resp. k realizaci 
            rezervovaného focení a vyřízení případných nároků, nejdéle však po dobu 3 let od posledního 
            kontaktu – ledaže vznikne smluvní vztah, v jehož rámci mohou být údaje uchovávány po dobu 
            trvání smlouvy a dále po dobu stanovenou právními předpisy (zejm. zákon o účetnictví, 
            zákon o DPH).
          </Text>
        </Box>

        {/* 7. Vaše práva */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            7. Vaše práva
          </Heading>
          <Text color={textColor} lineHeight={1.8} mb={2}>
            Podle GDPR máte následující práva, která můžete uplatnit zasláním e-mailu na{' '}
            <Link href={`mailto:${CONTACT_EMAIL}`} color={colorMode === 'dark' ? 'gray.300' : 'gray.700'} textDecoration="underline">
              {CONTACT_EMAIL}
            </Link>
            :
          </Text>
          <VStack align="start" pl={4} spacing={2}>
            <Text color={textColor}>
              <strong>Právo na přístup</strong> – můžete požádat o informaci, jaké údaje o vás zpracováváme.
            </Text>
            <Text color={textColor}>
              <strong>Právo na opravu</strong> – pokud jsou vaše údaje nepřesné nebo neúplné, máte právo požádat o jejich opravu.
            </Text>
            <Text color={textColor}>
              <strong>Právo na výmaz</strong> – můžete požádat o smazání svých osobních údajů, pokud pominul účel zpracování nebo odvoláte souhlas.
            </Text>
            <Text color={textColor}>
              <strong>Právo na omezení zpracování</strong> – v zákonem stanovených případech můžete požádat o omezení zpracování.
            </Text>
            <Text color={textColor}>
              <strong>Právo na přenositelnost</strong> – právo získat své údaje ve strukturovaném, strojově čitelném formátu.
            </Text>
            <Text color={textColor}>
              <strong>Právo vznést námitku</strong> – proti zpracování na základě oprávněného zájmu.
            </Text>
            <Text color={textColor}>
              <strong>Právo odvolat souhlas</strong> – kdykoli, aniž by tím byla dotčena zákonnost zpracování před odvoláním.
            </Text>
          </VStack>
          <Text color={textColor} lineHeight={1.8} mt={3}>
            Na vaši žádost odpovíme bez zbytečného odkladu, nejpozději do 30 dnů. Tato lhůta může být 
            ve složitých případech prodloužena o dalších 60 dnů, o čemž vás budeme informovat.
          </Text>
        </Box>

        {/* 8. Stížnost k ÚOOÚ */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            8. Právo podat stížnost
          </Heading>
          <Text color={textColor} lineHeight={1.8}>
            Pokud se domníváte, že zpracování vašich osobních údajů porušuje předpisy na ochranu 
            osobních údajů, máte právo podat stížnost u dozorového orgánu – v České republice je jím:
          </Text>
          <VStack align="start" pl={4} mt={2} spacing={1}>
            <Text color={textColor}><strong>Úřad pro ochranu osobních údajů (ÚOOÚ)</strong></Text>
            <Text color={textColor}>Pplk. Sochora 27, 170 00 Praha 7</Text>
            <Text color={textColor}>
              Web:{' '}
              <Link href="https://www.uoou.cz" isExternal color={colorMode === 'dark' ? 'gray.300' : 'gray.700'} textDecoration="underline">
                www.uoou.cz
              </Link>
            </Text>
          </VStack>
        </Box>

        {/* 9. Zabezpečení */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            9. Zabezpečení
          </Heading>
          <Text color={textColor} lineHeight={1.8}>
            Webové stránky jsou provozovány přes zabezpečené HTTPS připojení. Kontaktní i rezervační 
            formulář se odesílají prostřednictvím šifrovaného spojení. Přijímáme přiměřená technická 
            a organizační opatření k ochraně vašich osobních údajů před ztrátou, zneužitím nebo neoprávněným přístupem.
          </Text>
        </Box>

        {/* 10. Změny */}
        <Box w="100%">
          <Heading as="h2" size="md" fontWeight={400} color={headingColor} mb={3}>
            10. Změny zásad ochrany osobních údajů
          </Heading>
          <Text color={textColor} lineHeight={1.8}>
            Tyto zásady mohou být příležitostně aktualizovány. Datum poslední aktualizace je vždy uvedeno 
            v záhlaví tohoto dokumentu. Doporučujeme tuto stránku pravidelně navštěvovat.
          </Text>
        </Box>

        <Divider borderColor={colorMode === 'dark' ? 'gray.700' : 'gray.200'} />

        <Link
          href="/"
          fontSize="sm"
          color={colorMode === 'dark' ? 'gray.400' : 'gray.500'}
          letterSpacing="1px"
          textTransform="uppercase"
          _hover={{ color: headingColor }}
        >
          ← Zpět na hlavní stránku
        </Link>

      </VStack>
    </Container>
  );
};

export default PrivacyPolicy;
